/* eslint-disable react-hooks/set-state-in-effect */
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";

import { updateCompanyData } from "@/apis/companyList";
import { addCompany, usePickList } from "@/apis/emailMashup";
import { resetDataFetched } from "@/store/slices/companyList";
import { fetchCompanyList } from "@/store/thunks/companyListThunks";
import EmailUtils from "@/utils/EmailUtils";

import type { AppDispatch } from "@/store/store";

interface AddCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  email: string;
  name: string;
  phone?: string;
  companyTypeId?: string | null;
  companyComments?: string;
  id?: string;
  edit?: boolean;
  onSaveSuccess?: (email: string) => void;
}

export const AddCompanyDialog: React.FC<AddCompanyDialogProps> = ({
  open,
  onClose,
  email,
  name,
  phone: initialPhone = "",
  companyTypeId,
  companyComments,
  id,
  edit = false,
  onSaveSuccess,
}) => {
  const queryClient = useQueryClient();
  const [companyName, setCompanyName] = useState(name);
  const [companyEmail, setCompanyEmail] = useState(email);
  const [phone, setPhone] = useState("");
  const [typeId, setTypeId] = useState<string | null>(null);
  const [comments, setComments] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const [severity, setSeverity] = useState<"success" | "error">("error");

  const { data: picklist } = usePickList();
  const dispatch = useDispatch<AppDispatch>();

  const companyTypeOptions = useMemo(
    () =>
      picklist?.filter(
        (item) => item.parent_type === "company" && item.type === "type"
      ) || [],
    [picklist]
  );

  useEffect(() => {
    if (open) {
      if (edit) {
        setCompanyName(name);
        setPhone(initialPhone);
        setTypeId(companyTypeId || null);
        setComments(companyComments || "");
        return;
      }

      setCompanyName(name);
      setPhone(initialPhone);
      setTypeId(null);
      setComments("");
    }
  }, [open, name, initialPhone, edit, companyTypeId, companyComments]);

  const { mutate: createCompany, isPending } = useMutation({
    mutationFn: addCompany,
    onSuccess: (createdCompany) => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      dispatch(resetDataFetched());
      dispatch(fetchCompanyList());
      onSaveSuccess?.(createdCompany.email || "");
      onClose();
    },
    onError: (error) => console.error("Failed to add company:", error),
  });

  const { mutate: editCompany, isPending: isUpdating } = useMutation({
    mutationFn: (payload: any) => updateCompanyData(payload.id, payload.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      dispatch(resetDataFetched());
      dispatch(fetchCompanyList());
      onSaveSuccess?.(variables.data.email || "");
      onClose();
    },
    onError: (error) => console.error("Failed to update company:", error),
  });

  const handleSnackBarClose = useCallback(() => {
    setClientError(null);
    setSeverity("error");
  }, []);

  const isEmailValid = (emailValue: string) => {
    if (emailValue) {
      if (emailValue && !EmailUtils.isValidEmail(emailValue)) {
        setSeverity("error");
        setClientError(
          "Invalid email format. Please enter a valid email address."
        );
        return false;
      }
      setClientError(null);
      return true;
    }
  };

  const handleSubmit = () => {
    if (!companyName) return;
    const trimmedEmail = companyEmail.trim();
    if (trimmedEmail.length) {
      const result = isEmailValid(trimmedEmail);
      if (!result) {
        return;
      }
    }
    if (edit) {
      editCompany({
        id: id,
        data: {
          name: companyName,
          email: trimmedEmail,
          phone: phone,
          type_id: typeId,
          flag_comments: comments,
        },
      });
    } else {
      createCompany({
        name: companyName,
        email: trimmedEmail,
        phone,
        type_id: typeId || undefined,
        flag_comments: comments || undefined,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" onClick={(e) => e.stopPropagation()}>
      <DialogTitle>
        {edit ? "Edit Company" : "Add New Company"}
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Email"
            onChange={(e) => setCompanyEmail(e.target.value)}
            value={companyEmail}
            fullWidth
          />
          <TextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="company-type-select-label">Company Type</InputLabel>
            <Select
              labelId="company-type-select-label"
              label="Company Type"
              value={typeId || ""}
              onChange={(e) => setTypeId(e.target.value as string)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {companyTypeOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={isPending || isUpdating}
        >
          {edit ? "Update Company" : "Add Company"}
        </LoadingButton>
      </DialogActions>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!clientError}
        onClose={handleSnackBarClose}
        autoHideDuration={5000}
      >
        <Alert severity={severity}>{clientError} </Alert>
      </Snackbar>
    </Dialog>
  );
};
